import {
    DataWithLocation,
    DataWithLocationArray,
    GetWithLocationFilterArgs,
    ImsTimeCollection,
} from '../../../shared/collections/time/ims_time_collection.factory';
import { CachedEventRegistry } from '../../../shared/events/cached_event_registry';
import { VectorHeatMapContext } from '../../../views/view_types/view_utils';
import moment from 'moment-timezone';
import { clone } from 'lodash';
import { HeatmapDataRequester } from './HeatmapDataRequester';

/***
 * Chunks by timeframe instead of chunk size. Handy for certain queries to avoid
 * massive joins in the database over long time periods which can kill query time.
 *
 * In the best case this Requester will not perform as well as the LimitChunkedHeatmapDataRequester,
 * but in the worse case (long timeframe, lots of joins in query) this will perform better than the
 * LimitChunkedHeatmapDataRequester
 */
export class TimeChunkedHeatmapDataRequester implements HeatmapDataRequester {
    // This is still relevant and will be used in the query
    private _heatMapChunkSize: number;
    private _columnName: string;
    private _filterArgs: GetWithLocationFilterArgs;
    private _timeCollection: ImsTimeCollection;
    private _hoursRequested: number;
    private _heatMapListeners: CachedEventRegistry;
    private _totalOfFailedChunkRequests: number;
    private _reachedLimitOfTotalFailedChunks: boolean;
    private _chunksThatHaveFailed: Array<number>;
    private _maxPoints: number;
    private _heatMapDatabaseFailLimitPerChunk: number;
    private _heatMapDatabaseFailLimit: number;
    private _vectorHeatMapContext: VectorHeatMapContext | null;
    // All important time chunk size.
    private _initialHeatMapTimeChunkSizeSeconds: number;
    private _currentHeatMapTimeChunkSizeSeconds: number;
    private _heatMapTimeChunkSizeSecondsAdjustment: number;

    constructor(
        columnName: string,
        filterArgs: GetWithLocationFilterArgs,
        timeCollection: ImsTimeCollection,
        hoursRequested: number,
        heatmapListeners: CachedEventRegistry,
        maxPoints: number,
        heatmapChunkSize: number,
        heatMapDatabaseFailLimitPerChunk: number,
        heatMapDatabaseFailLimit: number,
        vectorHeatMapContext: VectorHeatMapContext | null,
        initialHeatMapTimeChunkSizeSeconds: number,
        heatMapTimeChunkSizeSecondsAdjustment: number,
    ) {
        this._columnName = columnName;
        this._filterArgs = filterArgs;
        this._timeCollection = timeCollection;
        this._hoursRequested = hoursRequested;
        this._maxPoints = maxPoints;
        this._heatMapListeners = heatmapListeners;
        this._totalOfFailedChunkRequests = 0;
        this._reachedLimitOfTotalFailedChunks = false;
        this._chunksThatHaveFailed = [];
        this._heatMapDatabaseFailLimitPerChunk = heatMapDatabaseFailLimitPerChunk;
        this._heatMapDatabaseFailLimit = heatMapDatabaseFailLimit;
        this._heatMapChunkSize = heatmapChunkSize;
        this._vectorHeatMapContext = vectorHeatMapContext;
        this._initialHeatMapTimeChunkSizeSeconds = initialHeatMapTimeChunkSizeSeconds;
        this._currentHeatMapTimeChunkSizeSeconds = initialHeatMapTimeChunkSizeSeconds;
        this._heatMapTimeChunkSizeSecondsAdjustment = heatMapTimeChunkSizeSecondsAdjustment;
    }

    async makeHeatMapRequests(): Promise<DataWithLocationArray> {
        let combinedDataWithLocation = {} as DataWithLocation;
        const filterArgs = clone(this._filterArgs);

        // This won't work without a defined timeframe so have some sanity checks.
        if (!filterArgs.startTime) {
            throw new Error('Expected filterArgs.startTime to be defined');
        }
        if (!filterArgs.endTime) {
            throw new Error('Expected filterArgs.endTime to be defined');
        }

        const originalStartMoment = moment(filterArgs.startTime);
        const originalEndMoment = moment(filterArgs.endTime);
        const totalSeconds = originalEndMoment.diff(originalStartMoment, 'seconds');

        // Not super efficient but it'll do!
        let currentStartMoment = originalEndMoment
            .clone()
            .subtract(this._currentHeatMapTimeChunkSizeSeconds, 'seconds');
        if (currentStartMoment.isBefore(originalStartMoment)) {
            currentStartMoment = originalStartMoment;
        }
        let currentEndMoment = originalEndMoment;

        let chunkNumber = 1;

        let isLastIteration = false;

        while (!isLastIteration) {
            // We've got enough points, exit early
            if (Object.keys(combinedDataWithLocation).length >= this._maxPoints) {
                break;
            }
            // There has been to many errors, exit early
            if (this._reachedLimitOfTotalFailedChunks) {
                break;
            }

            if (currentStartMoment.isSame(originalStartMoment)) {
                isLastIteration = true;
            }

            filterArgs.startTime = currentStartMoment.toISOString();
            filterArgs.endTime = currentEndMoment.toISOString();

            const chunkOfDataWithLocations = await this._fetchDataFromTimeCollection(
                clone(filterArgs),
                chunkNumber,
                currentStartMoment,
                currentEndMoment,
            );

            const receivedChunkSize = chunkOfDataWithLocations
                ? chunkOfDataWithLocations.length
                : 0;

            // Make a small adjustment to the length of time that we retrieve
            this._adjustCurrentHeatMapChunkSize(receivedChunkSize);

            if (receivedChunkSize === this._heatMapChunkSize) {
                // We've collected the exact limit - find the earliest timestamp in the
                // returned data and use that for the new endTime
                currentEndMoment = await this._getTimestampOfDataPoint(
                    chunkOfDataWithLocations[chunkOfDataWithLocations.length - 1].id,
                );
            } else {
                // Empty result, just shuffle ourselves back by the current
                // heat map chunk time window
                currentEndMoment = currentStartMoment;
            }

            // Adjust the current start moment to be relative to the current end moment
            currentStartMoment = currentEndMoment
                .clone()
                .subtract(this._currentHeatMapTimeChunkSizeSeconds, 'seconds');

            // Clamp the current start to the originally requested start
            if (currentStartMoment.isBefore(originalStartMoment)) {
                currentStartMoment = originalStartMoment.clone();
            }

            // add the retrieved data from this iteration to the previously collected data
            combinedDataWithLocation = this._addFetchedChunkToCombinedDataWithLocation(
                chunkOfDataWithLocations,
                combinedDataWithLocation,
            );

            // The total amount of time scanned divided by the total amount of time scanned...
            await this._heatMapListeners.notifyListeners(
                (100 * originalEndMoment.diff(currentEndMoment, 'seconds')) / totalSeconds,
            );

            chunkNumber++;
        }
        return this._buildCombinedDataWithLocationArray(combinedDataWithLocation);
    }

    private _adjustCurrentHeatMapChunkSize(receivedChunkSize: number): void {
        if (receivedChunkSize < this._heatMapChunkSize) {
            // We have received below the fetch limit, increase the time window to fetch a little more
            this._currentHeatMapTimeChunkSizeSeconds += this._heatMapTimeChunkSizeSecondsAdjustment;
        } else {
            // The max number of points we can receive will be equal to the heatmap chunk size, reduce
            // the chunk size a little so we can hopefully oscillate close to the fetch limit and not get
            // any failures
            this._currentHeatMapTimeChunkSizeSeconds -= this._heatMapTimeChunkSizeSecondsAdjustment;
            // Don't go below the initial size. If the initial size is too small it should be adjusted
            // accordingly
            this._currentHeatMapTimeChunkSizeSeconds = Math.max(
                this._currentHeatMapTimeChunkSizeSeconds,
                this._initialHeatMapTimeChunkSizeSeconds,
            );
        }
    }

    private async _fetchDataFromTimeCollection(
        filterArgs: GetWithLocationFilterArgs,
        chunkNumber: number,
        currentStartMoment: moment.Moment,
        currentEndMoment: moment.Moment,
    ): Promise<DataWithLocation | any> {
        let chunkFailCounter = 0;

        while (this._shouldRetryDatabaseFetch(chunkFailCounter)) {
            // We only want to try each chunk a certain amount of times (set in configService)
            // We want to limit the number of fails for all requests (set in configService)
            try {
                return await this._timeCollection.getWithLocation(
                    this._columnName,
                    filterArgs,
                    this._heatMapChunkSize,
                    this._vectorHeatMapContext,
                );
            } catch (e) {
                console.warn('failed heatmap data chunk request, retrying');
                this._totalOfFailedChunkRequests++;

                // To be safe, reset the time chunk size...
                this._currentHeatMapTimeChunkSizeSeconds = this._initialHeatMapTimeChunkSizeSeconds;

                filterArgs = clone(filterArgs);
                currentStartMoment = currentEndMoment
                    .clone()
                    .subtract(this._currentHeatMapTimeChunkSizeSeconds, 'seconds');
                filterArgs.startTime = currentStartMoment.toISOString();
            }
            chunkFailCounter++;
        }

        this._updateFailureCounters(chunkFailCounter, chunkNumber);
        // Return empty array because that's all that we've collected
        return [];
    }

    private _updateFailureCounters(chunkFailCounter: number, chunkNumber: number): void {
        if (this._reachedChunkRetryLimit(chunkFailCounter)) {
            this._chunksThatHaveFailed.push(chunkNumber);
        }
        this._reachedLimitOfTotalFailedChunks = this._reachedTotalFailedChunks();
    }

    private _buildCombinedDataWithLocationArray(
        combinedDataWithLocation: DataWithLocation,
    ): DataWithLocationArray {
        const combinedDataWithLocationArray = [];
        for (const key in combinedDataWithLocation) {
            if (combinedDataWithLocationArray.length < this._maxPoints) {
                combinedDataWithLocationArray.push(combinedDataWithLocation[key]);
            }
        }
        return combinedDataWithLocationArray;
    }

    private async _getTimestampOfDataPoint(id: number): Promise<moment.Moment> {
        const dataPointReturned = await this._timeCollection.getAsync(id);
        if (dataPointReturned) {
            return moment(dataPointReturned.resource.timestamp);
        } else {
            throw new Error("Can't find the last fetched point in the collection");
        }
    }

    private _addFetchedChunkToCombinedDataWithLocation(
        chunkOfDataWithLocation: DataWithLocationArray,
        allDataWithLocation: DataWithLocation,
    ): DataWithLocation {
        chunkOfDataWithLocation.map((dataChunk) => {
            allDataWithLocation[dataChunk.id] = dataChunk;
        });
        return allDataWithLocation;
    }

    private _reachedTotalFailedChunks() {
        return this._totalOfFailedChunkRequests === this._heatMapDatabaseFailLimit;
    }

    private _reachedChunkRetryLimit(chunkRetries: number): boolean {
        return chunkRetries === this._heatMapDatabaseFailLimitPerChunk;
    }

    private _shouldRetryDatabaseFetch(counter: number): boolean {
        return !this._reachedTotalFailedChunks() && !this._reachedChunkRetryLimit(counter);
    }

    get reachedLimitOfTotalFailedChunks(): boolean {
        return this._reachedLimitOfTotalFailedChunks;
    }

    get chunksThatHaveFailed(): Array<number> {
        return this._chunksThatHaveFailed;
    }
}
